import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';

import LayoutDashboard from 'components/Layout/Dashboard';
import DateRangePicker from 'components/DatePickers/DateRangePicker';
import { getAuditEvents, getMoreAuditEvents, emptyAuditEvents } from 'actions/AuditEvents/Get';
import { ReduxState } from '../../reducers/types';
import AuditEventsTable from 'components/AuditLogs/AuditEventsTable';
import AuditEventDetailDialog from 'components/AuditLogs/AuditEventDetailDialog';

const styles = require('./styles.pcss');

const DEFAULT_LIMIT = 50;

type Filters = {
  identifier: string;
  namespace: string;
  name: string;
  organizationId: string;
  userId: string;
  startDate: any;
  endDate: any;
};

const emptyFilters: Filters = {
  identifier: '',
  namespace: '',
  name: '',
  organizationId: '',
  userId: '',
  startDate: null,
  endDate: null,
};

function AuditLogs() {
  const dispatch = useDispatch();
  const events = useSelector((state: ReduxState) => (state as any).auditEvents.events);
  const isLoading = useSelector((state: ReduxState) => (state as any).auditEvents.isLoading);
  const isLoadingMore = useSelector((state: ReduxState) => (state as any).auditEvents.isLoadingMore);
  const hasMore = useSelector((state: ReduxState) => (state as any).auditEvents.hasMore);
  const cursor = useSelector((state: ReduxState) => (state as any).auditEvents.cursor);

  const [filters, setFilters] = React.useState<Filters>({ ...emptyFilters });
  const [selectedEvent, setSelectedEvent] = React.useState<any | null>(null);

  const buildParams = (currentFilters: Filters) => {
    const params: any = {
      limit: DEFAULT_LIMIT,
    };
    if (currentFilters.identifier) params.identifier = currentFilters.identifier;
    if (currentFilters.namespace) params.namespace = currentFilters.namespace;
    if (currentFilters.name) params.name = currentFilters.name;
    if (currentFilters.organizationId) params.organizationId = currentFilters.organizationId;
    if (currentFilters.userId) params.userId = currentFilters.userId;
    if (currentFilters.startDate) params.dateFrom = currentFilters.startDate.format('YYYY-MM-DD');
    if (currentFilters.endDate) params.dateTo = currentFilters.endDate.format('YYYY-MM-DD');
    return params;
  };

  React.useEffect(() => {
    dispatch(getAuditEvents(buildParams(emptyFilters)) as any);
    return () => {
      dispatch(emptyAuditEvents() as any);
    };
  }, []);

  const handleSearch = () => {
    dispatch(getAuditEvents(buildParams(filters)) as any);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleLoadMore = React.useCallback(() => {
    if (!cursor || isLoadingMore) return;
    dispatch(getMoreAuditEvents(buildParams(filters), cursor) as any);
  }, [cursor, isLoadingMore, filters, dispatch]);

  return (
    <LayoutDashboard full>
      <div className={styles.container}>
        <h1 className={styles.title}>Audit Logs</h1>

        <div className={styles.filters} onKeyDown={handleKeyDown}>
          <TextField
            label="Identifier"
            size="small"
            value={filters.identifier}
            onChange={(e) => setFilters((prev) => ({ ...prev, identifier: e.target.value }))}
            className={styles.filterInput}
          />
          <TextField
            label="Namespace"
            size="small"
            value={filters.namespace}
            onChange={(e) => setFilters((prev) => ({ ...prev, namespace: e.target.value }))}
            className={styles.filterInput}
          />
          <TextField
            label="Name"
            size="small"
            value={filters.name}
            onChange={(e) => setFilters((prev) => ({ ...prev, name: e.target.value }))}
            className={styles.filterInput}
          />
          <TextField
            label="Organization ID"
            size="small"
            value={filters.organizationId}
            onChange={(e) => setFilters((prev) => ({ ...prev, organizationId: e.target.value }))}
            className={styles.filterInput}
          />
          <TextField
            label="User ID"
            size="small"
            value={filters.userId}
            onChange={(e) => setFilters((prev) => ({ ...prev, userId: e.target.value }))}
            className={styles.filterInput}
          />
          <DateRangePicker
            label="Date Range"
            startDate={filters.startDate}
            endDate={filters.endDate}
            onChange={({ startDate, endDate }: any) => setFilters((prev) => ({ ...prev, startDate, endDate }))}
            className={styles.dateFilterInput}
            error=""
            autoCloseOnSelect
          />
          <Button
            variant="contained"
            disableElevation
            onClick={handleSearch}
            disabled={isLoading}
            className={styles.searchButton}
          >
            Search
          </Button>
        </div>

        {isLoading && (
          <div className={styles.loader}>
            <CircularProgress size={32} />
          </div>
        )}

        {!isLoading && events.length === 0 && (
          <p className={styles.empty}>No audit events found.</p>
        )}

        {!isLoading && events.length > 0 && (
          <AuditEventsTable
            events={events}
            isLoadingMore={isLoadingMore}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            onDetailsClick={setSelectedEvent}
          />
        )}
      </div>

      <AuditEventDetailDialog
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
      />
    </LayoutDashboard>
  );
}

export default AuditLogs;
